import React, { useState } from 'react';
import { Upload, Database, TrendingUp, FileText, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import DataIngestionModal from '../DataIngestionModal';
import { ingestCampaignResultData, ingestROIData } from '@/services/superAdminAPI';

/**
 * Data Ingestion Component for Database Tab
 * Provides UI for uploading Campaign Result and ROI data
 */
export function DataIngestionSection() {
  const [activeModal, setActiveModal] = useState(null); // 'campaign-result' | 'roi' | null
  const [lastUpload, setLastUpload] = useState({
    campaignResult: null,
    roi: null
  });

  const handleCampaignResultUpload = async (file, scheduleTime) => {
    const result = await ingestCampaignResultData(file, scheduleTime);
    setLastUpload(prev => ({ ...prev, campaignResult: new Date() }));
    return result;
  };

  const handleROIUpload = async (file, scheduleTime) => {
    const result = await ingestROIData(file, scheduleTime);
    setLastUpload(prev => ({ ...prev, roi: new Date() }));
    return result;
  };

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Database className="h-5 w-5 mr-2 text-blue-600" />
                Data Ingestion
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Upload and ingest data from Excel or CSV files
              </p>
            </div>
          </div>

          {/* Info Banner */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Upload Requirements</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Supported formats: Excel (.xlsx, .xls) or CSV (.csv)</li>
                <li><strong>Required:</strong> "Amplify Campaign Name" column (must match existing campaign names)</li>
                <li>Campaign names are matched case-insensitively</li>
                <li>File size limit: 10MB for optimal performance</li>
                <li>Remove empty rows before uploading</li>
                <li>Ensure column headers match expected field names</li>
              </ul>
              <p className="mt-2 text-xs font-medium text-blue-900">
                ⚠️ If records show as "extracted" but not "loaded", check the campaign names in your file match existing campaigns.
              </p>
            </div>
          </div>

          {/* Upload Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Campaign Result Upload Card */}
            <div className="border border-gray-200 rounded-lg p-5 hover:border-blue-400 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-gray-900">Campaign Result Data</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Upload campaign performance data</p>
                  </div>
                </div>
              </div>

              {lastUpload.campaignResult && (
                <div className="mb-3 text-xs text-gray-500">
                  Last upload: {lastUpload.campaignResult.toLocaleString()}
                </div>
              )}

              <button
                onClick={() => setActiveModal('campaign-result')}
                className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Campaign Result
              </button>

              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Required columns:</span> Amplify Campaign Name, Region, and other performance metrics
                </p>
              </div>
            </div>

            {/* ROI Upload Card */}
            <div className="border border-gray-200 rounded-lg p-5 hover:border-green-400 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-gray-900">ROI Data</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Upload return on investment data</p>
                  </div>
                </div>
              </div>

              {lastUpload.roi && (
                <div className="mb-3 text-xs text-gray-500">
                  Last upload: {lastUpload.roi.toLocaleString()}
                </div>
              )}

              <button
                onClick={() => setActiveModal('roi')}
                className="w-full flex items-center justify-center px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload ROI Data
              </button>

              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Required columns:</span> Amplify Campaign Name, Region, Brand, and ROI metrics
                </p>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h5 className="text-sm font-semibold text-gray-900 mb-2">Processing Information</h5>
            <div className="text-xs text-gray-600 space-y-1">
              <p>• Campaign names are matched case-insensitively against existing campaigns</p>
              <p>• Invalid records will be logged and can be reviewed after upload</p>
              <p>• Large datasets are processed in batches for optimal performance</p>
              <p>• You can schedule uploads for off-peak hours using the schedule option</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <DataIngestionModal
        isOpen={activeModal === 'campaign-result'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleCampaignResultUpload}
        title="Upload Campaign Result Data"
        description="Upload campaign performance data from Excel or CSV file"
      />

      <DataIngestionModal
        isOpen={activeModal === 'roi'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleROIUpload}
        title="Upload ROI Data"
        description="Upload return on investment data from Excel or CSV file"
      />
    </>
  );
}

export default DataIngestionSection;
